import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiMail,
  FiLock,
  FiUser,
  FiEye,
  FiEyeOff,
  FiShield,
  FiArrowRight,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

import SafeIcon from "../common/SafeIcon";
import { useClient } from "../context/ClientContext";

import {
  INVITATION_STORAGE_KEY,
} from "./AcceptInvitation";

const isValidToken = (token) =>
  typeof token === "string" &&
  /^[0-9a-f]{64}$/i.test(token);

function getInvitationToken(search) {
  const fromUrl = new URLSearchParams(search).get("token");

  if (isValidToken(fromUrl)) {
    try {
      sessionStorage.setItem(
        INVITATION_STORAGE_KEY,
        fromUrl
      );
    } catch {
      // Continue without storage.
    }

    return fromUrl;
  }

  try {
    const stored = sessionStorage.getItem(
      INVITATION_STORAGE_KEY
    );

    return isValidToken(stored) ? stored : null;
  } catch {
    return null;
  }
}

export default function ClientRegister() {
  const navigate = useNavigate();
  const location = useLocation();

  const { signupInvitedClient } = useClient();

  const invitationToken = getInvitationToken(
    location.search
  );

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmationRequired, setConfirmationRequired] =
    useState(false);

  const invitationPath = invitationToken
    ? `/accept-invitation?token=${encodeURIComponent(
        invitationToken
      )}`
    : "/accept-invitation";

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;

    setError("");
    setSuccess("");

    if (!invitationToken) {
      setError(
        "A valid invitation link is required to create a client account."
      );
      return;
    }

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim().toLowerCase();

    if (!firstName || !lastName) {
      setError("Please enter your first and last name.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (
      form.password.length < 8 ||
      !/[a-z]/.test(form.password) ||
      !/[A-Z]/.test(form.password) ||
      !/[0-9]/.test(form.password)
    ) {
      setError(
        "Password must contain at least 8 characters, including uppercase, lowercase, and a number."
      );
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const result = await signupInvitedClient({
        firstName,
        lastName,
        email,
        phone: form.phone.trim(),
        password: form.password,
        invitationToken,
      });

      if (!result?.success) {
        setError(
          result?.error ||
            "Unable to create your client account."
        );
        return;
      }

      if (result.data?.session) {
        navigate(invitationPath, {
          replace: true,
        });
        return;
      }

      setConfirmationRequired(true);

      setSuccess(
        "Please check your email and verify your account. After verification, sign in using the same email address to complete your invitation."
      );

      setForm((previous) => ({
        ...previous,
        password: "",
        confirmPassword: "",
      }));
    } catch (requestError) {
      console.error(
        "Client registration error:",
        requestError
      );

      setError(
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-2xl border-2 border-slate-100 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition-all focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10";

  return (
    
      <div
        className="
          flex
          h-screen
          h-[100dvh]
          w-full
          min-h-0
          items-start
          justify-center
          overflow-y-auto
          overflow-x-hidden
          overscroll-y-contain
          bg-slate-50
          px-4
          py-10
          font-sans
        "
      >

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-10"
      >
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
          <SafeIcon
            icon={FiShield}
            className="text-2xl text-emerald-700"
          />
        </div>

        <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
          ProfitYo · Client Portal
        </p>

        <h1 className="mb-3 text-3xl font-black text-slate-900">
          Create your client account
        </h1>

        <p className="mb-7 text-sm leading-7 text-slate-500">
          Create a secure account to access the organization
          your accounting team invited you to join.
        </p>

        {!invitationToken && (
          <div
            role="alert"
            className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          >
            This registration page requires an invitation.
            Please open the invitation link sent by your
            accounting team.
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
          >
            <SafeIcon
              icon={FiAlertCircle}
              className="mt-0.5 shrink-0"
            />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div
            role="status"
            className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
          >
            <SafeIcon
              icon={FiCheckCircle}
              className="mt-0.5 shrink-0"
            />
            <span>{success}</span>
          </div>
        )}

        {!confirmationRequired && (
          <form
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="client-first-name"
                  className="mb-2 block text-xs font-bold text-slate-600"
                >
                  First Name
                </label>

                <input
                  id="client-first-name"
                  name="firstName"
                  value={form.firstName}
                  onChange={updateField}
                  autoComplete="given-name"
                  required
                  maxLength={100}
                  placeholder="First name"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="client-last-name"
                  className="mb-2 block text-xs font-bold text-slate-600"
                >
                  Last Name
                </label>

                <input
                  id="client-last-name"
                  name="lastName"
                  value={form.lastName}
                  onChange={updateField}
                  autoComplete="family-name"
                  required
                  maxLength={100}
                  placeholder="Last name"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="client-email"
                className="mb-2 flex items-center gap-2 text-xs font-bold text-slate-600"
              >
                <SafeIcon icon={FiMail} />
                Invited Email Address
              </label>

              <input
                id="client-email"
                type="email"
                name="email"
                value={form.email}
                onChange={updateField}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                className={inputClass}
                placeholder="you@company.com"
              />

              <p className="mt-2 text-xs text-slate-400">
                Use the email address that received your
                invitation.
              </p>
            </div>

            <div>
              <label
                htmlFor="client-phone"
                className="mb-2 block text-xs font-bold text-slate-600"
              >
                Phone Number (Optional)
              </label>

              <input
                id="client-phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={updateField}
                autoComplete="tel"
                className={inputClass}
                placeholder="Your phone number"
              />
            </div>

            <div>
              <label
                htmlFor="client-password"
                className="mb-2 flex items-center gap-2 text-xs font-bold text-slate-600"
              >
                <SafeIcon icon={FiLock} />
                Password
              </label>

              <div className="relative">
                <input
                  id="client-password"
                  type={
                    showPassword ? "text" : "password"
                  }
                  name="password"
                  value={form.password}
                  onChange={updateField}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  className={`${inputClass} pr-12`}
                  placeholder="Create a password"
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowPassword((value) => !value)
                  }
                  aria-label={
                    showPassword
                      ? "Hide password"
                      : "Show password"
                  }
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  <SafeIcon
                    icon={
                      showPassword ? FiEyeOff : FiEye
                    }
                  />
                </button>
              </div>

              <p className="mt-2 text-xs text-slate-400">
                Minimum 8 characters with uppercase,
                lowercase, and a number.
              </p>
            </div>

            <div>
              <label
                htmlFor="client-confirm-password"
                className="mb-2 block text-xs font-bold text-slate-600"
              >
                Confirm Password
              </label>

              <div className="relative">
                <input
                  id="client-confirm-password"
                  type={
                    showConfirm ? "text" : "password"
                  }
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={updateField}
                  autoComplete="new-password"
                  required
                  className={`${inputClass} pr-12`}
                  placeholder="Confirm password"
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowConfirm((value) => !value)
                  }
                  aria-label={
                    showConfirm
                      ? "Hide confirmation password"
                      : "Show confirmation password"
                  }
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  <SafeIcon
                    icon={
                      showConfirm ? FiEyeOff : FiEye
                    }
                  />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !invitationToken}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading
                ? "Creating your account..."
                : "Create Client Account"}

              {!loading && (
                <SafeIcon icon={FiArrowRight} />
              )}
            </button>
          </form>
        )}

        <div className="mt-7 border-t border-slate-100 pt-6 text-center">
          <p className="mb-3 text-sm text-slate-500">
            Already have a ProfitYo account?
          </p>

          <Link
            to="/login"
            className="text-sm font-bold text-emerald-700 hover:text-emerald-800"
          >
            Sign in
          </Link>

          <div className="mt-5">
            <Link
              to={invitationPath}
              className="text-xs font-medium text-slate-500 hover:text-slate-800"
            >
              Back to invitation
            </Link>
          </div>
        </div>

        <div className="mt-7 flex items-center justify-center gap-2 text-xs text-slate-400">
          <SafeIcon icon={FiUser} />
          Secure client access by ProfitYo
        </div>
      </motion.div>
    </div>
  );
}